import React from 'react'
import { useNavigate } from 'react-router-dom'
import type { DashboardMetrics } from '../../hooks/useDashboard'
import { Card } from '../ui/Card'

interface AlertsPanelProps {
  alerts: DashboardMetrics['alerts']
  loading?: boolean
}

export const AlertsPanel: React.FC<AlertsPanelProps> = ({ alerts, loading }) => {
  const navigate = useNavigate()

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'high_cancel_rate': return '🚨'
      case 'slow_acceptance': return '⏰'
      case 'payment_failures': return '💳'
      case 'no_driver': return '🚗'
      case 'restaurant_offline': return '🏪'
      default: return '⚠️'
    }
  }

  const getAlertColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-50 border-red-200 text-red-800'
      case 'warning': return 'bg-yellow-50 border-yellow-200 text-yellow-800'
      default: return 'bg-gray-50 border-gray-200 text-gray-800'
    }
  }

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'critical': return 'badge-error'
      case 'warning': return 'badge-warning'
      default: return 'badge-info'
    }
  }

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-48"></div>
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </Card>
    )
  }

  if (!alerts || alerts.length === 0) {
    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Alerts</h3>
        <div className="text-center py-8">
          <div className="text-4xl mb-3">✅</div>
          <p className="text-green-600 font-medium">All systems operational</p>
          <p className="text-gray-500 text-sm mt-1">No alerts at this time</p>
        </div>
      </Card>
    )
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Alerts</h3>
        <span className="badge badge-error">{alerts.length}</span>
      </div>
      <div className="space-y-3">
        {alerts.map((alert, index) => (
          <div
            key={index}
            className={`p-4 rounded-lg border ${getAlertColor(alert.severity)} transition-all hover:shadow-md ${
              alert.restaurant_id ? 'cursor-pointer' : ''
            }`}
            onClick={() => {
              if (alert.restaurant_id) {
                navigate(`/restaurants/${alert.restaurant_id}`)
              }
            }}
          >
            <div className="flex items-start space-x-3">
              <div className="text-xl">{getAlertIcon(alert.type)}</div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <span className={`badge ${getSeverityBadge(alert.severity)} text-xs`}>
                    {alert.severity.toUpperCase()}
                  </span>
                  {alert.restaurant_name && (
                    <span className="text-xs font-medium">{alert.restaurant_name}</span>
                  )}
                </div>
                <p className="font-medium text-sm mb-1">{alert.message}</p>
                {alert.value !== undefined && alert.threshold !== undefined && (
                  <div className="flex items-center space-x-2 mt-2">
                    <div className="flex-1 bg-white rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          alert.severity === 'critical' ? 'bg-red-500' : 'bg-yellow-500'
                        }`}
                        style={{ width: `${Math.min((alert.value / alert.threshold) * 100, 100)}%` }}
                      ></div>
                    </div>
                    <span className="text-xs font-mono">
                      {alert.value.toFixed(1)} / {alert.threshold}
                    </span>
                  </div>
                )}
              </div>
              {alert.restaurant_id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate(`/restaurants/${alert.restaurant_id}`)
                  }}
                  className="text-sm font-medium text-admin-orange-500 hover:text-admin-orange-600 hover:underline"
                >
                  View →
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}
